// Download control for the Job Analytics panels.
// ----------------------------------------------------------------------------
// Rasterises a DOM subtree with html2canvas, then hands it back either as a PNG
// or wrapped in a PDF. Both libraries are already project dependencies and this
// mirrors the per-form export in formsanalytics.jsx, so the two screens produce
// consistent output.
//
// It captures what is CURRENTLY RENDERED. Job Analytics only offers this on the
// merged view, where the target is the single combined panel; if that panel is
// collapsed it exports as its header only, which the tooltip says rather than
// leaving it to be discovered.

import { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";

// Matches the page background so the capture does not come out transparent
// (PNG) or ringed in black (PDF).
const CAPTURE_BG = "#F3F4F6";

const rasterise = (el) =>
  html2canvas(el, {
    scale: 2,
    backgroundColor: CAPTURE_BG,
    useCORS: true,
    logging: false,
    // html2canvas clones the node into an offscreen iframe sized to the
    // viewport; without these a panel taller or wider than the window is cut.
    windowWidth: el.scrollWidth,
    windowHeight: el.scrollHeight,
  });

// Y offsets (canvas px) of every region the PDF must not break through — each
// chart card, measured relative to the captured element.
const blockRanges = (el, ratio) => {
  const rootTop = el.getBoundingClientRect().top;
  return [...el.querySelectorAll("[data-pdf-block]")].map((node) => {
    const r = node.getBoundingClientRect();
    return { top: (r.top - rootTop) * ratio, bottom: (r.bottom - rootTop) * ratio };
  });
};

// Largest cut <= limit that does not pass through a block. Cards sit side by
// side in a two-column grid, so a cut level with one card's bottom can still be
// inside its taller neighbour — hence testing every block rather than just
// snapping to the nearest boundary.
const findCut = (blocks, from, limit, hardFloor) => {
  const straddling = blocks.filter((b) => b.top < limit && b.bottom > limit);
  if (straddling.length === 0) return limit;

  // Pull back to the highest top among the blocks crossing the limit.
  const candidate = Math.min(...straddling.map((b) => b.top));
  // A single block taller than a page cannot be honoured; slice it rather than
  // loop forever.
  return candidate > from + hardFloor ? candidate : limit;
};

const saveDataUrl = (dataUrl, filename) => {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const ExportMenu = ({ targetRef, filename = "job-analytics", disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null); // "pdf" | "png" | null
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const exportPng = async () => {
    const el = targetRef?.current;
    if (!el) return;
    try {
      setBusy("png");
      const canvas = await rasterise(el);
      saveDataUrl(canvas.toDataURL("image/png"), `${filename}.png`);
    } catch (err) {
      console.error("Failed to export analytics image", err);
    } finally {
      setBusy(null);
      setOpen(false);
    }
  };

  const exportPdf = async () => {
    const el = targetRef?.current;
    if (!el) return;
    try {
      setBusy("pdf");
      const canvas = await rasterise(el);
      const pdf = new jsPDF("p", "mm", "a4");
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const imgW = pageW - margin * 2;

      // mm per canvas pixel, so page heights can be expressed in canvas space.
      const mmPerPx = imgW / canvas.width;
      const usableMm = pageH - margin * 2;
      const pageHeightPx = usableMm / mmPerPx;

      const ratio = canvas.height / el.scrollHeight;
      const blocks = blockRanges(el, ratio);

      // Cut the tall capture into page-sized slices that fall BETWEEN cards.
      // The old version advanced by a fixed page height and drew the same image
      // offset upwards, which put the break wherever it happened to land —
      // straight through a chart.
      const slice = document.createElement("canvas");
      const ctx = slice.getContext("2d");
      let y = 0;
      let first = true;

      while (y < canvas.height) {
        const limit = Math.min(y + pageHeightPx, canvas.height);
        const cut =
          limit >= canvas.height
            ? canvas.height
            : findCut(blocks, y, limit, pageHeightPx * 0.25);
        const sliceH = Math.max(1, Math.round(cut - y));

        slice.width = canvas.width;
        slice.height = sliceH;
        ctx.fillStyle = CAPTURE_BG;
        ctx.fillRect(0, 0, slice.width, slice.height);
        ctx.drawImage(canvas, 0, y, canvas.width, sliceH, 0, 0, canvas.width, sliceH);

        if (!first) pdf.addPage();
        pdf.addImage(
          slice.toDataURL("image/png"),
          "PNG",
          margin,
          margin,
          imgW,
          sliceH * mmPerPx,
        );
        first = false;
        y += sliceH;
      }

      pdf.save(`${filename}.pdf`);
    } catch (err) {
      console.error("Failed to export analytics PDF", err);
    } finally {
      setBusy(null);
      setOpen(false);
    }
  };

  const working = busy !== null;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        disabled={disabled || working}
        onClick={() => setOpen((v) => !v)}
        title="Download the merged panel as it is currently shown. Expand it first if it is collapsed."
        className="flex items-center gap-1 px-3 py-1 rounded-md border border-gray-200 text-gray-600 transition hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <DownloadIcon style={{ fontSize: 15 }} />
        {working ? "Preparing…" : "Download"}
      </button>

      {open && !working && (
        <div className="absolute right-0 z-50 mt-1 w-44 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
          <button
            type="button"
            onClick={exportPdf}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-100"
          >
            <PictureAsPdfIcon style={{ fontSize: 16 }} />
            Download as PDF
          </button>
          <button
            type="button"
            onClick={exportPng}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-100"
          >
            <ImageIcon style={{ fontSize: 16 }} />
            Download as image
          </button>
        </div>
      )}
    </div>
  );
};

export default ExportMenu;
